import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Handles Firebase Auth email actions: password reset, email verification, etc.
 * Firebase sends all email actions to a single URL, differentiated by the `mode` query parameter.
 */
const AuthActionPage: React.FC = () => {
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const continueUrl = searchParams.get('continueUrl') || '';
  const isFromIos = continueUrl.includes('/ios');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Email verification mode
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  useEffect(() => {
    if (mode !== 'verifyEmail' || !oobCode) return;

    const verifyEmail = async () => {
      setVerifyingEmail(true);
      try {
        await applyActionCode(auth, oobCode);
        setEmailVerified(true);
        // Auto-redirect iOS users back to the app via custom URL scheme
        if (isFromIos) {
          window.location.href = 'carouselshopai://verified';
        }
      } catch (err: any) {
        if (err.code === 'auth/expired-action-code') {
          setVerifyError('This verification link has expired. Please request a new one.');
        } else if (err.code === 'auth/invalid-action-code') {
          // Code already consumed - email was likely already verified
          setEmailVerified(true);
          if (isFromIos) {
            window.location.href = 'carouselshopai://verified';
          }
        } else {
          setVerifyError('Failed to verify email. Please try again.');
        }
      } finally {
        setVerifyingEmail(false);
      }
    };

    verifyEmail();
  }, [mode, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!oobCode) {
      setError('Invalid or missing reset code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await resetPassword(oobCode, newPassword);
      setSuccess(true);
    } catch (err: any) {
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Email verification mode
  if (mode === 'verifyEmail') {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            {verifyingEmail ? (
              <>
                <h2>Verifying Email</h2>
                <p className="login-subtitle">Please wait...</p>
              </>
            ) : emailVerified ? (
              <>
                <h2>Email Verified</h2>
                <p className="login-subtitle">
                  Your email has been verified successfully.
                  {isFromIos
                    ? ' Returning you to the Carousel Shop AI app...'
                    : ' You can now return to the app to continue.'}
                </p>
                {isFromIos ? (
                  <div className="login-footer">
                    <p><a href="carouselshopai://verified">Open Carousel Shop AI</a></p>
                  </div>
                ) : (
                  <div className="login-footer">
                    <p><Link to="/login">Go to Login</Link></p>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2>Verification Failed</h2>
                <p className="login-subtitle">{verifyError}</p>
                <div className="login-footer">
                  <p><Link to="/login">Back to Login</Link></p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!oobCode) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <h2>Invalid Link</h2>
            <p className="login-subtitle">
              This link is invalid or has expired.
            </p>
            <div className="login-footer">
              <p><Link to="/login">Back to Login</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-form">
          <h2>Set New Password</h2>
          <p className="login-subtitle">Enter your new password below.</p>

          {success ? (
            <>
              <div style={{
                padding: '16px',
                backgroundColor: 'var(--delft-ice)',
                borderRadius: '8px',
                color: 'var(--charcoal)',
                fontSize: '0.875rem',
                marginBottom: '16px',
                textAlign: 'center',
              }}>
                Your password has been reset successfully.
              </div>
              <div className="login-footer">
                <p><Link to="/login">Sign in with your new password</Link></p>
              </div>
            </>
          ) : (
            <>
              {error && (
                <div className="error-message">
                  {error}
                  {(error.includes('expired') || error.includes('invalid')) && (
                    <div style={{ marginTop: '8px' }}>
                      <Link to="/forgot-password">Request a new reset link</Link>
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <button type="submit" disabled={loading} className="login-btn" style={{ width: '100%' }}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>

              <div className="login-footer">
                <p><Link to="/login">Back to Login</Link></p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthActionPage;
